/**
 * RoomServiceDetailPage Component
 * Phase D2.4: Room Service ordering inside Concierge Grid
 *
 * Shows menu categories and items with add-to-cart functionality.
 * Supports keyboard navigation (TV remote style) and voice commands.
 * Voice input is handled locally (spacebar does NOT open Ask Zaxia).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { ShoppingCart, Plus, Minus, X, Check, Mic, Trash2 } from 'lucide-react';
import {
  allMenuItems,
  menuCategories,
  calculateOrderTotal,
  formatOrderForSpeech,
  findMenuItemByVoice,
  parseQuantityFromVoice,
  type OrderItem,
  type MenuItem,
} from '@/data/roomServiceMenu';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';
import type { CreateBookingRequest } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

interface RoomServiceDetailPageProps {
  isActive: boolean;
  onBack: () => void;
  onOrderComplete: (booking: CreateBookingRequest) => Promise<void>;
  onVoiceHintsChange?: (hints: VoiceHintConfig | null) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(amount: number): string {
  return `€${amount.toFixed(0)}`;
}

// Room service images for slideshow
const ROOM_SERVICE_IMAGES = [
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=800&h=600&fit=crop',
];

// =============================================================================
// COMPONENT
// =============================================================================

export function RoomServiceDetailPage({
  isActive,
  onBack,
  onOrderComplete,
  onVoiceHintsChange,
}: RoomServiceDetailPageProps) {
  const [focusedItemIndex, setFocusedItemIndex] = useState(0);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Voice recognition state
  const [voiceState, setVoiceState] = useState<'idle' | 'listening' | 'processing'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceFeedback, setVoiceFeedback] = useState<string | null>(null);
  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');

  // Update global voice hints when page opens
  useEffect(() => {
    onVoiceHintsChange?.({
      title: 'Room Service',
      hints: ['Add cheeseburger', 'Place order', 'Cancel order'],
      variant: 'default',
    });

    // Reset hints when leaving
    return () => {
      onVoiceHintsChange?.(null);
    };
  }, [onVoiceHintsChange]);

  // Auto-advance image slideshow
  useEffect(() => {
    if (ROOM_SERVICE_IMAGES.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % ROOM_SERVICE_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Calculate total
  const orderTotal = calculateOrderTotal(orderItems);
  const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);

  // Add item to order
  const addItemToOrder = useCallback((item: MenuItem, quantity: number = 1) => {
    setOrderItems((prev) => {
      const existing = prev.find((o) => o.menuItem.id === item.id);
      if (existing) {
        return prev.map((o) =>
          o.menuItem.id === item.id
            ? { ...o, quantity: o.quantity + quantity }
            : o
        );
      }
      return [...prev, { menuItem: item, quantity }];
    });
  }, []);

  // Update item quantity
  const updateItemQuantity = useCallback((itemId: string, delta: number) => {
    setOrderItems((prev) =>
      prev
        .map((o) =>
          o.menuItem.id === itemId
            ? { ...o, quantity: Math.max(0, o.quantity + delta) }
            : o
        )
        .filter((o) => o.quantity > 0)
    );
  }, []);

  // Remove item from order
  const removeItemFromOrder = useCallback((itemId: string) => {
    setOrderItems((prev) => prev.filter((o) => o.menuItem.id !== itemId));
  }, []);

  // Clear entire order
  const clearOrder = useCallback(() => {
    setOrderItems([]);
  }, []);

  // Submit order
  const handleSubmitOrder = useCallback(async () => {
    if (orderItems.length === 0 || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Calculate delivery time (30-45 mins from now)
      const now = new Date();
      const deliveryTime = new Date(now.getTime() + 40 * 60 * 1000);

      const bookingRequest: CreateBookingRequest = {
        serviceId: 'room-service',
        serviceName: 'Room Service',
        category: 'ROOM_SERVICE',
        datetime: deliveryTime.toISOString(),
        durationMinutes: 15,
        price: orderTotal,
        location: 'In-Room Delivery',
        notes: orderItems.map(o => `${o.quantity}× ${o.menuItem.name}`).join('\n'),
      };

      await onOrderComplete(bookingRequest);
      setShowConfirmation(true);

      // Reset and go back after showing confirmation
      setTimeout(() => {
        setOrderItems([]);
        setShowConfirmation(false);
        onBack();
      }, 3000);
    } catch (error) {
      console.error('[ZAXIA] Room service order failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [orderItems, orderTotal, isSubmitting, onOrderComplete, onBack]);

  // ==========================================================================
  // VOICE RECOGNITION (local, prevents Ask Zaxia from opening)
  // Phase D4.5.2: Uses ZaxiaVoiceEngine for command processing
  // ==========================================================================

  const handleVoiceAction = useCallback((action: VoiceAction, transcript: string) => {
    console.log('[ROOM SERVICE] Handling voice action:', action.type, transcript);

    switch (action.type) {
      // Navigation: go back
      case 'CLOSE':
      case 'RS_BACK':
        zaxiaVoice.speak('Going back.');
        setVoiceFeedback('Going back...');
        setTimeout(() => onBack(), 500);
        break;

      // Cancel/clear order
      case 'CANCEL':
      case 'RS_CANCEL_ORDER':
        if (orderItems.length > 0) {
          clearOrder();
          zaxiaVoice.speak('Order cancelled. Your cart is now empty.');
          setVoiceFeedback('Order cancelled');
        } else {
          zaxiaVoice.speak('Your cart is already empty.');
          setVoiceFeedback('Cart already empty');
        }
        break;

      // Confirm/place order
      case 'CONFIRM':
      case 'RS_CONFIRM_ORDER':
        if (orderItems.length > 0) {
          zaxiaVoice.speak(`Placing your order for ${formatOrderForSpeech(orderItems)}.`);
          setVoiceFeedback('Placing order...');
          handleSubmitOrder();
        } else {
          zaxiaVoice.speak('Your cart is empty. Try saying "add cheeseburger" to order.');
          setVoiceFeedback('Cart is empty');
        }
        break;

      // Remove item
      case 'RS_REMOVE_ITEM': {
        const itemToRemove = findMenuItemByVoice(action.payload.itemText);
        if (itemToRemove) {
          const existingOrder = orderItems.find(o => o.menuItem.id === itemToRemove.id);
          if (existingOrder) {
            removeItemFromOrder(itemToRemove.id);
            zaxiaVoice.speak(`Removed ${itemToRemove.name} from your order.`);
            setVoiceFeedback(`Removed ${itemToRemove.name}`);
          } else {
            zaxiaVoice.speak(`${itemToRemove.name} is not in your order.`);
            setVoiceFeedback(`${itemToRemove.name} not in cart`);
          }
        } else {
          zaxiaVoice.speak("I couldn't find that item.");
          setVoiceFeedback('Item not found');
        }
        break;
      }

      // Add item (from engine or fallback)
      case 'RS_ADD_ITEM': {
        const menuItem = findMenuItemByVoice(action.payload.itemText);
        if (menuItem) {
          const quantity = parseQuantityFromVoice(action.payload.itemText);
          addItemToOrder(menuItem, quantity);
          const qtyText = quantity > 1 ? `${quantity} ` : '';
          zaxiaVoice.speak(`Added ${qtyText}${menuItem.name} to your order.`);
          setVoiceFeedback(`Added ${qtyText}${menuItem.name}`);
        } else {
          zaxiaVoice.speak("I couldn't find that item on the menu.");
          setVoiceFeedback('Item not found');
        }
        break;
      }

      // Unknown command - try to find a menu item in the transcript
      case 'UNKNOWN': {
        const menuItem = findMenuItemByVoice(action.payload.transcript);
        if (menuItem) {
          const quantity = parseQuantityFromVoice(action.payload.transcript);
          addItemToOrder(menuItem, quantity);
          const qtyText = quantity > 1 ? `${quantity} ` : '';
          zaxiaVoice.speak(`Added ${qtyText}${menuItem.name} to your order.`);
          setVoiceFeedback(`Added ${qtyText}${menuItem.name}`);
        } else {
          zaxiaVoice.speak("I didn't catch that. Try saying 'add cheeseburger' or 'place order'.");
          setVoiceFeedback("Couldn't understand");
        }
        break;
      }

      // BOOK_RESTAURANT fires for food-related words (breakfast, lunch, etc.) from
      // the global voice engine. Inside room service, treat it as an item order.
      case 'BOOK_RESTAURANT': {
        const itemText = action.payload.transcript ?? transcript;
        const menuItem = findMenuItemByVoice(itemText);
        if (menuItem) {
          const quantity = parseQuantityFromVoice(itemText);
          addItemToOrder(menuItem, quantity);
          const qtyText = quantity > 1 ? `${quantity} ` : '';
          zaxiaVoice.speak(`Added ${qtyText}${menuItem.name} to your order.`);
          setVoiceFeedback(`Added ${qtyText}${menuItem.name}`);
        } else {
          zaxiaVoice.speak("I couldn't find that item on the menu.");
          setVoiceFeedback('Item not found');
        }
        break;
      }

      // CONCIERGE_INTENT can fire if a menu item name accidentally matches a
      // concierge keyword (e.g. "sparkling" containing "spa" pre-boundary fix).
      // Treat as item order attempt using the raw transcript as fallback.
      case 'CONCIERGE_INTENT': {
        const menuItem = findMenuItemByVoice(transcript);
        if (menuItem) {
          const quantity = parseQuantityFromVoice(transcript);
          addItemToOrder(menuItem, quantity);
          const qtyText = quantity > 1 ? `${quantity} ` : '';
          zaxiaVoice.speak(`Added ${qtyText}${menuItem.name} to your order.`);
          setVoiceFeedback(`Added ${qtyText}${menuItem.name}`);
        } else {
          zaxiaVoice.speak("I didn't catch that. Try saying 'add cheeseburger' or 'place order'.");
          setVoiceFeedback("Couldn't understand");
        }
        break;
      }

      // Global actions handled by parent
      default:
        console.log('[ROOM SERVICE] Unhandled action type:', action.type);
        break;
    }
  }, [orderItems, addItemToOrder, removeItemFromOrder, handleSubmitOrder, onBack, clearOrder]);

  const processVoiceCommand = useCallback((transcript: string) => {
    console.log('[ROOM SERVICE] Processing voice command:', transcript);

    // Use the central voice engine to parse the command
    const voiceContext = createVoiceContext('room_service', {
      subContext: {
        roomServiceHasItems: orderItems.length > 0,
      },
    });
    const action = processVoiceInput(transcript, voiceContext);

    handleVoiceAction(action, transcript);
  }, [orderItems.length, handleVoiceAction]);

  const startVoiceListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      setVoiceFeedback('Voice not supported');
      return;
    }

    // Cancel any ongoing speech
    zaxiaVoice.cancel();

    // Cancel any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setVoiceTranscript('');
    transcriptRef.current = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) {
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setVoiceTranscript(currentTranscript);
      transcriptRef.current = currentTranscript;
    };

    recognition.onerror = () => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    };

    recognition.onend = () => {
      // Recognition ended naturally
    };

    recognition.start();
  }, []);

  const stopVoiceAndProcess = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }

    const finalTranscript = transcriptRef.current.trim();

    if (!finalTranscript) {
      setVoiceState('idle');
      setVoiceFeedback(null);
      return;
    }

    setVoiceState('processing');
    processVoiceCommand(finalTranscript);

    // Clear feedback after a delay
    setTimeout(() => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    }, 2000);
  }, [processVoiceCommand]);

  // Clear voice feedback after timeout
  useEffect(() => {
    if (voiceFeedback) {
      const timer = setTimeout(() => setVoiceFeedback(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [voiceFeedback]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if showing confirmation
      if (showConfirmation) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onBack();
          break;

        case 'ArrowUp':
          e.preventDefault();
          setFocusedItemIndex((prev) =>
            prev > 0 ? prev - 1 : allMenuItems.length - 1
          );
          break;

        case 'ArrowDown':
          e.preventDefault();
          setFocusedItemIndex((prev) =>
            prev < allMenuItems.length - 1 ? prev + 1 : 0
          );
          break;

        case 'Enter':
          e.preventDefault();
          const item = allMenuItems[focusedItemIndex];
          if (item) {
            addItemToOrder(item, 1);
          }
          break;

        case ' ':
          // Space starts voice input (prevents Ask Zaxia from opening)
          e.preventDefault();
          e.stopPropagation();
          if (voiceState === 'idle') {
            startVoiceListening();
          }
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && voiceState === 'listening') {
        e.preventDefault();
        e.stopPropagation();
        stopVoiceAndProcess();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Use capture
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [
    isActive,
    focusedItemIndex,
    orderItems,
    showConfirmation,
    isSubmitting,
    voiceState,
    addItemToOrder,
    handleSubmitOrder,
    startVoiceListening,
    stopVoiceAndProcess,
    onBack,
  ]);

  // Focus container on mount
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive]);

  // Confirmation overlay
  if (showConfirmation) {
    return (
      <motion.div
        ref={containerRef}
        tabIndex={-1}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center p-8"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6"
        >
          <Check className="w-10 h-10 text-green-400" />
        </motion.div>
        <h2 className="text-2xl font-semibold text-white mb-2">Order Confirmed!</h2>
        <p className="text-white/60 text-center">
          Your {formatOrderForSpeech(orderItems)} will arrive in 30-45 minutes.
        </p>
        <p className="text-zaxia-teal text-sm mt-4">
          Total: {formatCurrency(orderTotal)}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="room-service-detail h-full flex flex-col outline-none"
    >
      {/* Cart indicator */}
      {totalItems > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rs-cart-bar flex items-center justify-end gap-2 px-6 py-2 border-b border-white/[0.06] flex-shrink-0"
        >
          <ShoppingCart className="w-4 h-4 text-zaxia-teal" />
          <span className="text-sm font-medium text-white">
            {totalItems} {totalItems === 1 ? 'item' : 'items'}
          </span>
          <span className="text-sm text-zaxia-teal font-semibold">
            {formatCurrency(orderTotal)}
          </span>
        </motion.div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Image slideshow - left side; hides when ordering starts so menu keeps its space */}
        <motion.div
          className="relative overflow-hidden flex-shrink-0"
          animate={{ width: orderItems.length > 0 ? 0 : '40%' }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <AnimatePresence mode="wait">
            <img
              key={currentImageIndex}
              src={ROOM_SERVICE_IMAGES[currentImageIndex]}
              alt="Room Service"
              className="absolute inset-0 w-full h-full object-cover slide-img-fade"
            />
          </AnimatePresence>
          {ROOM_SERVICE_IMAGES.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {ROOM_SERVICE_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/40 w-1.5'
                  }`}
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Menu — compact 2-column grid, fits all items without scrolling */}
        <div className="rs-menu-panel flex-1 flex flex-col overflow-hidden">
          <div className="px-3 py-2 flex flex-col gap-2">
            {menuCategories.map((category) => (
              <div key={category.id}>
                <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">
                  {category.name}
                </div>
                <div className="flex flex-col gap-1">
                  {category.items.map((item) => {
                    const allIdx = allMenuItems.findIndex((i) => i.id === item.id);
                    const orderItem = orderItems.find((o) => o.menuItem.id === item.id);
                    const isFocused = focusedItemIndex === allIdx;
                    return (
                      <motion.div
                        key={item.id}
                        className={clsx(
                          'rs-menu-item flex items-center justify-between px-3 py-2 rounded-lg border transition-all cursor-pointer',
                          isFocused
                            ? 'bg-zaxia-teal/10 border-zaxia-teal/30 keyboard-focused'
                            : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.04]'
                        )}
                        animate={{ scale: isFocused ? 1.01 : 1 }}
                        onClick={() => { setFocusedItemIndex(allIdx); addItemToOrder(item, 1); }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-white truncate leading-tight">{item.name}</div>
                          <div className="text-[10px] text-white/40">{formatCurrency(item.price)}</div>
                        </div>
                        {orderItem ? (
                          <div className="flex items-center gap-1 ml-1.5 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); updateItemQuantity(item.id, -1); }}
                              className="w-6 h-6 rounded-md bg-white/[0.08] hover:bg-white/[0.12] flex items-center justify-center"
                            >
                              <Minus className="w-2.5 h-2.5 text-white/60" />
                            </button>
                            <span className="w-5 text-center text-xs font-semibold text-white">
                              {orderItem.quantity}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); updateItemQuantity(item.id, 1); }}
                              className="w-6 h-6 rounded-md bg-zaxia-teal/20 hover:bg-zaxia-teal/30 flex items-center justify-center"
                            >
                              <Plus className="w-2.5 h-2.5 text-zaxia-teal" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-md bg-white/[0.04] flex items-center justify-center ml-1.5 flex-shrink-0">
                            <Plus className="w-2.5 h-2.5 text-white/30" />
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order summary sidebar */}
        <AnimatePresence>
          {orderItems.length > 0 && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 260, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="border-l border-white/[0.04] overflow-hidden"
            >
              <div className="rs-order-sidebar w-[260px] h-full flex flex-col">
                <div className="px-4 py-3 border-b border-white/[0.04]">
                  <h3 className="text-sm font-medium text-white/60">Your Order</h3>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 hide-scrollbar">
                  {orderItems.map((orderItem) => (
                    <div
                      key={orderItem.menuItem.id}
                      className="flex items-center justify-between py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-white truncate">
                          {orderItem.quantity}× {orderItem.menuItem.name}
                        </div>
                        <div className="text-[10px] text-white/40">
                          {formatCurrency(orderItem.menuItem.price * orderItem.quantity)}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItemFromOrder(orderItem.menuItem.id)}
                        className="w-6 h-6 rounded-lg hover:bg-white/[0.08] flex items-center justify-center ml-2"
                      >
                        <X className="w-3 h-3 text-white/40" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="px-4 py-3 border-t border-white/[0.04]">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-white/60">Total</span>
                    <span className="font-semibold text-zaxia-teal">
                      {formatCurrency(orderTotal)}
                    </span>
                  </div>

                  <button
                    onClick={handleSubmitOrder}
                    disabled={isSubmitting}
                    className={clsx(
                      'w-full py-2.5 font-medium text-sm rounded-xl transition-all flex items-center justify-center gap-2',
                      isSubmitting
                        ? 'bg-white/[0.08] text-white/40 cursor-not-allowed'
                        : 'bg-zaxia-teal/20 hover:bg-zaxia-teal/30 text-zaxia-teal'
                    )}
                  >
                    {isSubmitting ? (
                      <>Placing Order...</>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Place Order
                      </>
                    )}
                  </button>

                  {/* Cancel Order button */}
                  <button
                    onClick={clearOrder}
                    disabled={isSubmitting}
                    className="w-full py-2 mt-2 font-medium text-xs rounded-xl transition-all flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Cancel Order
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer with voice indicator */}
      <div className="px-6 py-3 border-t border-white/[0.04]">
        {/* Voice indicator - shows when listening or has feedback */}
        <AnimatePresence>
          {(voiceState !== 'idle' || voiceFeedback) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center justify-center gap-3 mb-3"
            >
              {voiceState === 'listening' && (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center"
                  >
                    <Mic className="w-4 h-4 text-red-400" />
                  </motion.div>
                  <span className="text-sm text-white/60">
                    {voiceTranscript || 'Listening...'}
                  </span>
                </>
              )}
              {voiceState === 'processing' && (
                <span className="text-sm text-white/60">Processing...</span>
              )}
              {voiceState === 'idle' && voiceFeedback && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-zaxia-teal"
                >
                  {voiceFeedback}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </motion.div>
  );
}
